/**
 * The files on one item or mission: thumbnails and downloads, plus a drop zone (and paste
 * anywhere on the page while it is shown). Every async state: loading, unavailable (an older
 * hub), empty, uploading, error (per file, with Retry) and live (SSE keeps it current).
 */
import { useMemo, useState } from 'react';
import { formatBytes } from '@orgus/protocol';
import type { AttachmentTarget } from '@orgus/protocol';
import { AttachmentInput, AttachmentList, ErrorState, Skeleton, toUiError, toast } from '@orgus/ui';
import type { AttachmentView, UiError } from '@orgus/ui';
import { useOrgus } from '../../lib/store.ts';
import { canChange, removeAttachment, toView, uploaderFor, useAttachmentsOn, useAttachmentsState, useCaller } from './data.ts';

export interface AttachmentsPanelProps {
  target: AttachmentTarget;
  /** Take screenshots pasted anywhere on the page (one panel per view). */
  pasteAnywhere?: boolean;
  /** Open this attachment in the lightbox (a Markdown image was clicked). */
  openId?: string | null;
  onOpenChange?: (id: string | null) => void;
  /** What the files are for, under the drop zone. */
  hint?: string;
}

export function AttachmentsPanel({ target, pasteAnywhere, openId, onOpenChange, hint }: AttachmentsPanelProps) {
  const state = useAttachmentsState();
  const phase = useOrgus((s) => s.phase);
  const files = useAttachmentsOn(target);
  const caller = useCaller();
  const [removing, setRemoving] = useState<string[]>([]);
  const [error, setError] = useState<UiError | null>(null);
  const views = useMemo(() => files.map(toView), [files]);
  const upload = useMemo(() => uploaderFor(target), [target]);

  if (phase === 'loading') return <Skeleton className="block h-16 w-full" />;
  if (!state) {
    return <p className="text-ui text-fg-subtle">Update Orgus on the hub machine to attach files.</p>;
  }
  const remove = async (a: AttachmentView) => {
    setRemoving((r) => [...r, a.id]);
    setError(null);
    try {
      await removeAttachment(a.id);
      toast.success('Attachment removed', a.name);
    } catch (e) {
      setError(toUiError(e));
    } finally {
      setRemoving((r) => r.filter((x) => x !== a.id));
    }
  };
  const viewer = caller?.role === 'viewer';
  return (
    <div className="flex flex-col gap-3">
      <AttachmentList items={views} canRemove={(a) => canChange(caller, a.by)} onRemove={remove} removing={removing} openId={openId} onOpenChange={onOpenChange} />
      {error && <ErrorState variant="inline" error={error} />}
      {viewer ? (
        files.length === 0 && <p className="text-ui text-fg-subtle">No attachments</p>
      ) : (
        <AttachmentInput
          upload={upload}
          onUploaded={(a) => toast.success('Attached', a.name)}
          paste={pasteAnywhere ? 'document' : 'none'}
          maxBytes={state.usage.maxFile}
          hint={hint ?? `Screenshots, logs and files up to ${formatBytes(state.usage.maxFile)}. They are copied to the machine when a mission starts.`}
        />
      )}
    </div>
  );
}
